import posthog from '@leyan/lytics';
import { message } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  generateLazadaAuthUrl,
  getLazadaStandaloneAuthResult,
  LazadaStandaloneAuthResultBody,
} from 'services/api/admin';
import { AuthShopOpenMode } from 'services/api/flagr';
import { v4 as uuidV4 } from 'uuid';
import { PlatformShopAuthWayType } from '../components/PlatformShopAuthWay';
import useOpenURL from './useOpenURL';
import { useTransAppProductName } from 'hooks/useTrans';

const useLazadaAuthShop = () => {
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();

  const [loading, setLoading] = useState<boolean>(false);
  const [uuids, setUuids] = useState<string[]>([]);
  const [result, setResult] = useState<LazadaStandaloneAuthResultBody>();

  const openURL = useOpenURL();

  const getLazadaAuthURL = useCallback(
    async (
      authWay: PlatformShopAuthWayType,
      departments?: number[],
      openMode?: AuthShopOpenMode,
    ) => {
      try {
        setLoading(true);
        const uuid = uuidV4();
        const res = await generateLazadaAuthUrl(uuid, authWay, departments, openMode);
        setLoading(false);
        setUuids((u) => [...u, uuid]);
        if (res?.data?.code === 200) {
          return res?.data?.data?.authUrl;
        }
        throw new Error('请求 Lazada 授权链接失败');
      } catch (err) {
        setLoading(false);
        message.error(t('authShop.获取Lazada授权链接失败'));
        return undefined;
      }
    },
    [t],
  );

  // 复制lazada授权链接
  const copyLazadaURL = useCallback(
    async (authWay: PlatformShopAuthWayType, departments?: number[], noPrefix = false) => {
      try {
        const authURL = await getLazadaAuthURL(authWay, departments);
        if (authURL) {
          const template = noPrefix
            ? authURL
            : appProductNameT('authShop.copyLinkTemplate3', { platform: 'Lazada' }) + authURL;
          await navigator.clipboard.writeText(template);
          message.success(t('authShop.复制成功'));
          return true;
        }
        throw new Error('请求失败');
      } catch (err) {
        message.error(t('authShop.复制链接失败'));
        return false;
      }
    },
    [getLazadaAuthURL, t, appProductNameT],
  );

  const openAuthUrl = useCallback(
    async (authWay: PlatformShopAuthWayType, departments: number[], openMode: AuthShopOpenMode) => {
      try {
        const authURL = await getLazadaAuthURL(authWay, departments, openMode);
        if (authURL) {
          openURL(authURL, openMode);
          return true;
        }
        throw new Error('请求失败');
      } catch (err) {
        message.error(t('authShop.打开链接失败'));
        return false;
      }
    },
    [getLazadaAuthURL, openURL, t],
  );

  const copyLazadaURLB = useCallback(
    async (url: string, authWay: PlatformShopAuthWayType, guideUrl?: string) => {
      try {
        if (authWay === PlatformShopAuthWayType.Official) {
          await navigator.clipboard.writeText(url);
          message.success(t('authShop.复制成功'));
        } else {
          const template = `${t(
            'Hi，欢迎使用ChatPlusAI，根据Lazada官方规定，所有本土店铺必须登录服务市场并订购第三方应用后，才能进行授权，请点击链接前往 Lazada服务市场\n' +
              '温馨提示：若链接失效，请到ChatPlusAI客户端重新复制；若已登录非授权店铺，需要先退出，重新登录需要授权的店铺账号',
          )}
${t('Lazada服务市场链接（请在日常登录店铺后台的浏览器粘贴使用）：')} ${url}
${t('详细步骤指引：')} ${guideUrl}
`;
          await navigator.clipboard.writeText(template);
          message.success(t('authShop.复制成功'));
        }
        return true;
      } catch (err) {
        message.error(t('authShop.复制链接失败'));
        return false;
      }
    },
    [t],
  );

  const openAuthUrlB = useCallback(
    async (url: string, openMode: AuthShopOpenMode) => {
      try {
        openURL(url, openMode);
        return true;
      } catch (err) {
        message.error(t('authShop.打开链接失败'));
        return false;
      }
    },
    [openURL, t],
  );

  const checkAuthReulst = useCallback((uuid: string) => {
    return getLazadaStandaloneAuthResult(uuid)
      .then((r) => r.data.data)
      .catch((e) => {
        posthog.capture('$authReulstError', {
          platform: 'lazada',
          error: e,
        });
        return null;
      });
  }, []);
  return {
    loading,
    copyLazadaURL,
    openAuthUrl,
    checkAuthReulst,
    uuids,
    setUuids,
    result,
    setResult,
    openAuthUrlB,
    copyLazadaURLB,
  };
};

export default useLazadaAuthShop;
